import React from 'react';
import { Box, Typography } from '@mui/material';
import { DocDialog } from '../common/DocDialog';

interface OrganizationsDocDialogProps {
    open: boolean;
    handleClose: () => void;
}

const OrganizationsDocDialog: React.FC<OrganizationsDocDialogProps> = ({ open, handleClose }) => {
    return (
        <DocDialog open={open} title="Organizations Guide" handleClose={handleClose}>
            <Typography variant="h6" gutterBottom>Overview</Typography>
            <Typography variant="body2" paragraph>
                An organization groups users under a customer or business unit. It does two jobs: it scopes
                role assignments, so a user can hold a role inside one organization without holding it
                everywhere, and it defines the set of applications that the organization's administrators
                are allowed to work with.
            </Typography>
            <Typography variant="body2" paragraph>
                Organizations exist in both Auth0 and the identity database. Creating one here creates it in
                Auth0 first and saves it locally only if Auth0 accepted it, so the two stay in step. Only the
                name and display name are managed on this page — all other organization settings, such as
                connections and branding, are configured in Auth0.
            </Typography>

            <Typography variant="h6" gutterBottom>Organization Fields</Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2, '& li': { mb: 0.5 } }}>
                <Typography component="li" variant="body2">
                    <strong>Name</strong> (required, up to 50 characters) — the internal identifier, and it must
                    be unique. Auth0 restricts the format: lowercase letters, digits, hyphens and underscores
                    only, starting with a letter or digit. For example <code>acme-logistics</code> is valid,
                    while <code>Acme Logistics</code> is not.
                </Typography>
                <Typography component="li" variant="body2">
                    <strong>Display Name</strong> (required, up to 255 characters) — the friendly name shown
                    throughout the admin app, including the organization selector on the Users page. This one
                    is free-form, so use normal capitalization and spacing.
                </Typography>
            </Box>

            <Typography variant="h6" gutterBottom>Organization Applications</Typography>
            <Typography variant="body2" paragraph>
                The toggles in the <strong>Organization Applications</strong> section decide which applications
                administrators belonging to this organization can see and assign to their users. Only
                applications configured for the platform can be listed here. Tenant administrators are not
                limited by this list — they always see every application.
            </Typography>
            <Typography variant="body2" paragraph>
                <strong>An organization with no applications enabled is the most common configuration
                mistake.</strong> Its administrators will not be able to see or assign a single application,
                which usually looks like a broken permissions bug rather than an empty list. The dialog warns
                you whenever the list is empty — treat that warning as a task, not a notice.
            </Typography>
            <Typography variant="body2" paragraph>
                Toggling an application stages the change locally; nothing is sent until you click
                <strong> Save</strong>. Closing the dialog without saving discards the staged toggles.
            </Typography>

            <Typography variant="h6" gutterBottom>Creating an Organization</Typography>
            <Typography variant="body2" paragraph>
                Click <strong>New Organization</strong>, enter the name and display name, enable the
                applications this organization should have, then <strong>Save</strong>.
            </Typography>
            <Typography variant="body2" paragraph>
                The organization and its application list are saved in two steps. In the rare case where the
                organization is created but applying the application list fails, a message tells you so —
                reopen the organization and set the applications again. The organization itself is already
                created, so do not create it a second time.
            </Typography>

            <Typography variant="h6" gutterBottom>Editing an Organization</Typography>
            <Typography variant="body2" paragraph>
                Click any row to open the organization. Its current application list is loaded fresh from the
                server each time. If that load fails, the toggles stay disabled on purpose — this prevents a
                Save from replacing the real list with an empty one that was never actually read. Close the
                dialog and reopen it to retry.
            </Typography>

            <Typography variant="h6" gutterBottom>Deleting an Organization</Typography>
            <Typography variant="body2" paragraph>
                Open the organization, then use the menu in the top-right corner of the dialog and choose
                <strong> Delete</strong>. You will be asked to confirm. Two rules apply:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2, '& li': { mb: 0.5 } }}>
                <Typography component="li" variant="body2">
                    <strong>An organization with members cannot be deleted.</strong> Remove every user from it
                    first, then delete the organization.
                </Typography>
                <Typography component="li" variant="body2">
                    The <strong>Logistics Plus organization is protected</strong> and can never be deleted, so
                    it offers no Delete option at all.
                </Typography>
            </Box>

            <Typography variant="h6" gutterBottom>Audit History</Typography>
            <Typography variant="body2" paragraph>
                The <strong>Audit</strong> column on each row opens that organization's full history. Alongside
                name and display name edits, each application you enable or disable is recorded as its own
                entry naming the application, so you can see exactly when an organization gained or lost
                access to something.
            </Typography>

            <Typography variant="h6" gutterBottom>Important Notes</Typography>
            <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <Typography component="li" variant="body2">
                    Only tenant administrators can reach this page and manage organizations.
                </Typography>
                <Typography component="li" variant="body2">
                    Membership is managed from the user side, not here: use a user's
                    <strong> Organizations</strong> tab, or the Users page actions
                    <strong> Add to Organization</strong> and <strong>Import Users</strong> for bulk changes.
                </Typography>
                <Typography component="li" variant="body2">
                    Renaming an organization does not affect its membership or its application list.
                </Typography>
                <Typography component="li" variant="body2">
                    Enabling an application for an organization does not grant it to anyone. Each user still
                    needs that application enabled on their own <strong>Applications</strong> tab.
                </Typography>
            </Box>
        </DocDialog>
    );
};

export default OrganizationsDocDialog;
